/* eslint-disable @eslint-react/static-components --
 * The icon lookup returns a module-level component, so its reference is stable
 * across renders and nothing remounts. The rule cannot see that through the call.
 */
import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DESTINATION_KINDS,
  DESTINATION_TYPES,
  SUBSCRIBABLE_EVENTS,
  type CreateDestinationInput,
  type Destination,
  type DestinationDescriptor,
  type DestinationFieldDescriptor,
  type DestinationKind,
  type NotificationEventType,
  type SubscribableEvent,
  type UpdateDestinationInput,
} from '@tracearr/shared';
import type { PagesTranslations } from '@tracearr/translations';
import { Loader2, Send } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useCreateDestination,
  useTestDestination,
  useTestUnsavedDestination,
  useUpdateDestination,
} from '@/hooks/queries/useDestinations';
import { iconFor } from './destinationIcons';

type CreatableKind = CreateDestinationInput['type'];

/** Everything else reaches a destination through an automation, not a subscription. */
const subscribable = (event: NotificationEventType): event is SubscribableEvent =>
  (SUBSCRIBABLE_EVENTS as readonly NotificationEventType[]).includes(event);

/** Field labels are plain strings on the shared descriptor; the pages resource decides which exist. */
type FieldLabel = keyof PagesTranslations['settings']['destinations']['fields'];
type FieldHint = keyof PagesTranslations['settings']['destinations']['hints'];

function isCreatable(kind: DestinationKind): kind is CreatableKind {
  return !DESTINATION_TYPES[kind].builtin;
}

const CREATABLE_KINDS = DESTINATION_KINDS.filter(isCreatable);

interface DestinationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  destination?: Destination;
  onCreated?: (destination: Destination) => void;
}

export function DestinationDialog({
  open,
  onOpenChange,
  mode,
  destination,
  onCreated,
}: DestinationDialogProps) {
  const { t } = useTranslation(['pages', 'common']);
  const createDestination = useCreateDestination();
  const updateDestination = useUpdateDestination();
  const testSaved = useTestDestination();
  const testUnsaved = useTestUnsavedDestination();

  const [kind, setKind] = useState<DestinationKind | null>(null);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [events, setEvents] = useState<SubscribableEvent[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [edited, setEdited] = useState<Record<string, boolean>>({});
  const [cleared, setCleared] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && destination) {
      const opened: DestinationDescriptor = DESTINATION_TYPES[destination.type];
      const stored: Record<string, string> = {};
      for (const field of opened.fields) {
        const value = destination.config?.[field.key];
        if (typeof value === 'string') stored[field.key] = value;
      }
      setKind(destination.type);
      setName(destination.name);
      setEnabled(destination.enabled);
      setEvents(destination.events.filter(subscribable));
      setValues(stored);
    } else {
      setKind(null);
      setName('');
      setEnabled(true);
      setEvents([]);
      setValues({});
    }
    setEdited({});
    setCleared({});
    setDirty(false);
    setError(null);
  }, [open, mode, destination]);

  const descriptor: DestinationDescriptor | null = kind ? DESTINATION_TYPES[kind] : null;
  const isBuiltin = destination?.builtin ?? false;
  const isSaving = createDestination.isPending || updateDestination.isPending;
  const isTesting = testSaved.isPending || testUnsaved.isPending;

  /** A stored secret reads back as null, so an untouched blank box means "keep what the server has". */
  const keepsStoredSecret = (key: string): boolean =>
    mode === 'edit' &&
    !cleared[key] &&
    (values[key] ?? '') === '' &&
    (destination?.secretsSet.includes(key) ?? false);

  const isFilled = (field: DestinationFieldDescriptor): boolean =>
    (values[field.key] ?? '').trim() !== '' || keepsStoredSecret(field.key);

  const canSave =
    name.trim() !== '' && (descriptor?.fields ?? []).filter((f) => f.required).every(isFilled);

  const selectKind = (next: CreatableKind) => {
    const picked: DestinationDescriptor = DESTINATION_TYPES[next];
    const defaults: Record<string, string> = {};
    for (const field of picked.fields) {
      if (field.default !== undefined) defaults[field.key] = field.default;
    }
    setKind(next);
    setName(t(`pages:settings.destinations.types.${DESTINATION_TYPES[next].label}`));
    setEvents([...SUBSCRIBABLE_EVENTS]);
    setValues(defaults);
  };

  const setFieldValue = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setEdited((prev) => ({ ...prev, [key]: true }));
    setCleared((prev) => ({ ...prev, [key]: false }));
    setDirty(true);
  };

  const clearSecret = (key: string) => {
    setValues((prev) => ({ ...prev, [key]: '' }));
    setEdited((prev) => ({ ...prev, [key]: true }));
    setCleared((prev) => ({ ...prev, [key]: true }));
    setDirty(true);
  };

  const toggleViolations = (checked: boolean) => {
    setEvents(checked ? [...SUBSCRIBABLE_EVENTS] : []);
    setDirty(true);
  };

  /** Every field, for create and for the unsaved test. */
  const fullConfig = (): Record<string, string> =>
    Object.fromEntries(
      (descriptor?.fields ?? []).map((field) => [field.key, (values[field.key] ?? '').trim()])
    );

  /** Only what the user touched: null clears a secret, an omitted key keeps it. */
  const configPatch = (): Record<string, string | null> => {
    const patch: Record<string, string | null> = {};
    for (const field of descriptor?.fields ?? []) {
      if (cleared[field.key]) {
        patch[field.key] = null;
        continue;
      }
      if (!edited[field.key]) continue;
      const value = (values[field.key] ?? '').trim();
      if (value === '') {
        if (keepsStoredSecret(field.key)) continue;
        patch[field.key] = null;
        continue;
      }
      patch[field.key] = value;
    }
    return patch;
  };

  const handleSave = async () => {
    setError(null);
    try {
      if (mode === 'create') {
        if (!kind || !isCreatable(kind)) return;
        const created = await createDestination.mutateAsync({
          name: name.trim(),
          type: kind,
          config: fullConfig(),
          events,
          enabled,
        });
        onCreated?.(created);
      } else if (destination) {
        const data: UpdateDestinationInput = { name: name.trim(), enabled, events };
        const patch = configPatch();
        if (!isBuiltin && Object.keys(patch).length > 0) data.config = patch;
        await updateDestination.mutateAsync({ id: destination.id, data });
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleTest = async () => {
    setError(null);
    try {
      if (mode === 'edit' && destination) {
        await testSaved.mutateAsync(destination.id);
      } else if (kind) {
        await testUnsaved.mutateAsync({ type: kind, config: fullConfig() });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const title =
    mode === 'create'
      ? t('pages:settings.destinations.add')
      : `${t('common:actions.edit')} ${destination?.name ?? ''}`.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('pages:settings.destinations.description')}</DialogDescription>
        </DialogHeader>

        {descriptor === null ? (
          <div className="grid grid-cols-2 gap-3 py-2 sm:grid-cols-3">
            {CREATABLE_KINDS.map((creatable) => {
              const Icon = iconFor(creatable);
              return (
                <Button
                  key={creatable}
                  variant="outline"
                  className="h-auto flex-col gap-2 py-4"
                  onClick={() => selectKind(creatable)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm">
                    {t(`pages:settings.destinations.types.${DESTINATION_TYPES[creatable].label}`)}
                  </span>
                </Button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-2">
            <Field data-invalid={name.trim() === ''}>
              <FieldLabel htmlFor="destination-name">
                {t('common:labels.name')}
                <span className="text-destructive ml-1">*</span>
              </FieldLabel>
              <Input
                id="destination-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setDirty(true);
                }}
                aria-invalid={name.trim() === ''}
              />
              {name.trim() === '' && <FieldError>{t('common:validation.required')}</FieldError>}
            </Field>

            <Field orientation="horizontal">
              <FieldLabel htmlFor="destination-enabled">{t('common:states.enabled')}</FieldLabel>
              <Switch
                id="destination-enabled"
                checked={enabled}
                onCheckedChange={(checked) => {
                  setEnabled(checked);
                  setDirty(true);
                }}
              />
            </Field>

            <Field orientation="horizontal">
              <FieldContent>
                <FieldLabel htmlFor="destination-violations">
                  {t('pages:settings.destinations.receiveViolations')}
                </FieldLabel>
                <FieldDescription>
                  {t('pages:settings.destinations.receiveViolationsHint')}
                </FieldDescription>
              </FieldContent>
              <Switch
                id="destination-violations"
                checked={events.includes('violation_detected')}
                onCheckedChange={toggleViolations}
              />
            </Field>

            {descriptor.fields.map((field) => {
              const inputId = `destination-${field.key}`;
              const stored = keepsStoredSecret(field.key);
              const missing = field.required && !isFilled(field);
              const inputProps = {
                id: inputId,
                placeholder: stored
                  ? t('pages:settings.destinations.secretSet')
                  : field.placeholder,
                value: values[field.key] ?? '',
                onChange: (e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue(field.key, e.target.value),
                'aria-invalid': missing,
              };

              return (
                <Field key={field.key} data-invalid={missing}>
                  <FieldLabel htmlFor={inputId}>
                    {t(`pages:settings.destinations.fields.${field.label as FieldLabel}`)}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </FieldLabel>
                  {field.input === 'secret' ? (
                    <PasswordInput {...inputProps} />
                  ) : (
                    <Input {...inputProps} />
                  )}
                  {field.hint && !stored && (
                    <FieldDescription>
                      {t(`pages:settings.destinations.hints.${field.hint as FieldHint}`)}
                    </FieldDescription>
                  )}
                  {stored && (
                    <FieldDescription>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => clearSecret(field.key)}
                      >
                        {t('pages:settings.destinations.clearSecret')}
                      </Button>
                    </FieldDescription>
                  )}
                  {missing && <FieldError>{t('common:validation.required')}</FieldError>}
                </Field>
              );
            })}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:actions.cancel')}
          </Button>
          {descriptor !== null && !isBuiltin && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="outline"
                      onClick={handleTest}
                      disabled={isTesting || !canSave || (mode === 'edit' && dirty)}
                    >
                      {isTesting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {t('pages:settings.destinations.test')}
                    </Button>
                  </span>
                </TooltipTrigger>
                {mode === 'edit' && dirty ? (
                  <TooltipContent>{t('pages:settings.destinations.testHint')}</TooltipContent>
                ) : !canSave ? (
                  <TooltipContent>{t('pages:settings.destinations.testNeedsForm')}</TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
          )}
          {descriptor !== null && (
            <Button onClick={handleSave} disabled={!canSave || isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? t('common:states.saving') : t('common:actions.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
